import { CRM_SALUTATION } from 'app_config';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { Button } from 'components/common';
import Select from 'react-select'
import { toast } from 'react-toastify';
import { createContact, updateContact} from 'redux/crm/contactSlice';
import { useDispatch } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import {isValidPhoneNumber} from "libphonenumber-js"
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from 'react-router-dom';
import { makeEmployeeLoaderWithDetails } from 'helpers/debounce';
import EmployeeSelectField from 'components/common/select/EmployeeSelectField';
import { FaBan, FaSave } from 'react-icons/fa';

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const ContactForm = ({ customer, contact, handleCancel, onSuccess }) => {
  const {control, register, reset, handleSubmit, formState: { errors}, setValue } = useForm();

  const contactUrl = useMemo(() => "/crm/contacts", []);
  const customerUrl = useMemo(() => "/crm/customers", []);
  const salutationOptions = useMemo(() => CRM_SALUTATION.map((option) => ({ value: option, label: option })), []);
  // Load all active employees (value === label === name) for the contact selects.
  const employeeOptions = useMemo(() => makeEmployeeLoaderWithDetails(), []);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const defaultSalutationOption = useMemo(() => (
    contact ? { value: contact.salutation, label: contact.salutation } : salutationOptions[1]
  ), [contact, salutationOptions]);


  const defaultMainContactOption = useMemo(() => {
    if (contact && contact.main_contact) {
      return { value: contact.main_contact, label: contact.main_contact };
    }
    return null;
  }, [contact]);

  const defaultSecondaryContactOption = useMemo(() => {
    if (contact && contact.secondary_contact) {
      return contact.secondary_contact.map((option) => ({  value: option, label: option }));
    }
    return null;
  }, [contact]);

  useEffect(() => {
    if (contact) {
      // Edit: hydrate every field from the existing contact (incl. salutation).
      Object.entries(contact).forEach(([key, value]) => {
        setValue(key, value);
      });
    } else {
      // Create: default salutation to "Mr.".
      setValue('salutation', salutationOptions[1].value);
    }
  }, [contact, setValue, salutationOptions]);

  const onSubmit = async (data) => {
    if (!validate(data)) return;

    try {
      const customerId = customer ? customer.id : null;
      let contactId = contact ? contact.id : null;

      if (!contact) {
        const response = await dispatch(createContact({ customerId, data }));
        if (response.error) return; // rejected → slice already surfaces the error
        contactId = response.payload.data.id;
        reset();
      }
      else {
        const obj_params = customer ? { customerId, contactId, data } : { contactId, data };
        const response = await dispatch(updateContact(obj_params));
        if (response.error) return;
      }

      // When embedded (e.g. in a Drawer) defer to the caller; otherwise navigate.
      if (onSuccess) {
        onSuccess(contactId);
        return;
      }

      customer ? navigate(`${customerUrl}/${customerId}`) : navigate(`${contactUrl}/${contactId}`);
    }
    catch(error) {
      toast.error(error.message);
    }
  };

  const validatePhoneNumber = (number) => {
    if (number === undefined || number === "" || number === null) return undefined

    let countryCode = customer ? customer.country : 'VN'
    if (!isValidPhoneNumber(number.toString(), countryCode)) {
      return `Invalid phone number with country code ${countryCode}.`;
    }
    return true
  }

  const validate = (data) => {
    if ((data.email === '' || data.email === undefined) && (data.mobile === undefined || data.mobile === '')) {
      toast.error("Email or mobile must be required.")
      return false
    }
    return true
  }

  const fieldRow = (label, children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-3 tw-items-center">
      <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 lg:tw-text-right">
        {label}
      </div>
      <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
        {children}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="tw-text-sm">
      {fieldRow('Salutation', (
        <>
          <Controller
            control={control}
            name="salutation"
            render={() => (
              <Select
                closeMenuOnSelect={true}
                options={salutationOptions}
                defaultValue={defaultSalutationOption}
                placeholder='Select...'
                classNamePrefix="react-select"
                onChange={selectedOption => setValue('salutation', selectedOption.value, {shouldValidate: true})}
              />
            )}
            rules={{
              required: {
                value: true,
                message: 'Salutation is required'
              }
            }}
          />
          <span className="tw-text-red-600">
            {errors.salutation && errors.salutation.message}
          </span>
        </>
      ))}

      {fieldRow('Contact name', (
        <div className="tw-grid tw-grid-cols-12 tw-gap-2">
          <div className="tw-col-span-12 sm:tw-col-span-6">
            <input
              type="text"
              name="first_name"
              placeholder="First name"
              className={INPUT_CLASSES}
              {...register("first_name", {
                required: "First name must be required."
              })}
            />
            <span className="tw-text-red-600">
              {errors.first_name && errors.first_name.message}
            </span>
          </div>
          <div className="tw-col-span-12 sm:tw-col-span-6">
            <input
              type="text"
              name="last_name"
              placeholder="Last name"
              className={INPUT_CLASSES}
              {...register("last_name")}
            />
          </div>
        </div>
      ))}

      {fieldRow('Title', (
        <input
          type="text"
          name="position"
          placeholder="Title"
          className={INPUT_CLASSES}
          {...register("position")}
        />
      ))}

      {fieldRow('Department', (
        <input
          type="text"
          name="department"
          placeholder="Department"
          className={INPUT_CLASSES}
          {...register("department")}
        />
      ))}

      {fieldRow('Email', (
        <>
          <input
            type="email"
            name="email"
            placeholder="example@pebsteel.com.vn"
            className={INPUT_CLASSES}
            {...register('email', {
              //required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: 'Invalid email address'
              }
            })}
          />
          <span className="tw-text-red-600">
            {errors.email && errors.email.message}
          </span>
        </>
      ))}

      {fieldRow('Phone number', (
        <>
          <Controller
            control={control}
            name="mobile"
            render={({field: {ref, ...field}}) => (
              <PhoneInput
                {...field}
                inputProps={{ref}}
                country={customer ? customer.country?.toLowerCase() : 'vn' }
                autoFormat={true}
                countryCodeEditable={contact ? true : false}
                enableSearch={true}
                disableSearchIcon={true}
                inputStyle={{width: '100%'}}
                searchStyle={{width: '95%'}}
              />
            )}
            rules={{ validate: validatePhoneNumber }}
          />
          <span className="tw-text-red-600">
            {errors.mobile && errors.mobile.message}
          </span>
        </>
      ))}

      {fieldRow('Main contact', (
        <EmployeeSelectField
          name="main_contact"
          control={control}
          loadOptions={employeeOptions}
          required
          defaultValue={defaultMainContactOption}
          placeholder="Select or input name..."
        />
      ))}

      {fieldRow('Secondary contact', (
        <EmployeeSelectField
          name="secondary_contact"
          control={control}
          loadOptions={employeeOptions}
          isMulti
          defaultValue={defaultSecondaryContactOption}
          placeholder="Select..."
        />
      ))}

      {fieldRow('Other contact', (
        <input
          type="text"
          name="other_contact"
          placeholder="Other contact"
          className={INPUT_CLASSES}
          {...register("other_contact")}
        />
      ))}

      {fieldRow('Personal page', (
        <input
          type="text"
          name="personal_page"
          placeholder="Personal page"
          className={INPUT_CLASSES}
          {...register("personal_page")}
        />
      ))}

      <div className="tw-my-2">
        <div className="tw-flex tw-justify-center">
          <Button size="sm" type="submit" variant="primary"><FaSave /> Save</Button>
          <Button variant="danger" size="sm" className="tw-ml-2" onClick={handleCancel}><FaBan /> Cancel</Button>
        </div>
      </div>
    </form>
  )
}

ContactForm.propTypes = {
  customer: PropTypes.object,
  contact: PropTypes.object,
  handleCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

export default ContactForm;
